import { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../icons/Icon";
import PwPopover from "../PwPopover";
import {
  MATRIX_ROWS,
  ALL_SUB_ITEMS,
  applyAutoBumps,
  deriveFloors,
  floorRootSources,
  resolveMatrixToPermissions,
  reverseResolvePermissions,
} from "../../config/matrix";

/**
 * MatrixPicker — None/View/Edit dropdowns per sub-area.
 *
 * Controlled component: `value: string[]` is the resolved permissions
 * array stored on the role doc; emits `onChange(nextPermissions)` after
 * every user interaction.
 *
 * The picker applies `applyAutoBumps` to the local selections on every
 * level change, so cross-row dependencies (e.g. Process Payroll Edit
 * needing `emp:view` and `emp:pii:view`) light up in the UI the moment
 * the admin clicks. This is more honest about what's being granted than
 * deferring the cascade to save time.
 *
 * Selections + customPermissions are held as INTERNAL STATE, synced from
 * `value` on mount and on genuine external changes (e.g. async edit-mode
 * load). They are NOT re-derived from `value` after our own emit —
 * doing so would clobber state when two cells share identical grants
 * (Time Tracking View ↔ OT Approval View both grant
 * `time:view + emp:view`), because reverse-resolve would attribute the
 * same perms to both rows.
 *
 * See PRD_UAM_CustomRoles2.md §4.3 (auto-bumps as resolver-internal),
 * §5 for matrix specs, and §9 Phase 7 for the Editor wiring contract.
 * Visual reference: MOCKUP/settings-custom-roles.html — Section 4.
 */

const LEVELS_DISPLAY = ["none", "view", "edit"];
const LEVEL_LABEL = { none: "None", view: "View", edit: "Edit" };
const levelRank = (level) => LEVELS_DISPLAY.indexOf(level);
const formatFloorSources = (sources) =>
  sources.map((s) => `${s.label} (${LEVEL_LABEL[s.level]})`).join(", ");
const SECTION_ICON = {
  employees: "users",
  "time-attendance": "clock",
  payroll: "wallet",
  settings: "settings",
};

function setsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

const MatrixPicker = ({ value, onChange, disabled = false }) => {
  const safeValue = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value],
  );

  // Initial reverse-resolution — only on first mount. The sync effect
  // below handles subsequent external changes via lastEmittedRef.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initial = useMemo(() => reverseResolvePermissions(safeValue), []);
  const [selections, setSelections] = useState(initial.selections);
  const [customPermissions, setCustomPermissions] = useState(
    initial.customPermissions,
  );

  const lastEmittedRef = useRef(null);

  useEffect(() => {
    if (
      lastEmittedRef.current !== null &&
      setsEqual(new Set(lastEmittedRef.current), new Set(safeValue))
    ) {
      return;
    }
    const resolved = reverseResolvePermissions(safeValue);
    setSelections(resolved.selections);
    setCustomPermissions(resolved.customPermissions);
  }, [safeValue]);

  const emit = (nextSelections, nextCustom) => {
    const nextPerms = resolveMatrixToPermissions(nextSelections, nextCustom);
    lastEmittedRef.current = nextPerms;
    onChange(nextPerms);
  };

  const handleLevelChange = (subItemId, nextLevel) => {
    if (disabled) return;
    if (selections[subItemId] === nextLevel) return;
    // Apply autoBumps to the new selections so cross-row dependencies
    // (e.g. Process Payroll = Edit → Sensitive Info = View) light up
    // immediately in the UI. Without this the cascade only surfaces
    // after save→reopen, which hides PII grants from admins.
    const nextSelections = applyAutoBumps({
      ...selections,
      [subItemId]: nextLevel,
    });
    setSelections(nextSelections);
    emit(nextSelections, customPermissions);
  };

  const handleResetAll = () => {
    if (disabled) return;
    const nextSelections = {};
    ALL_SUB_ITEMS.forEach((s) => {
      nextSelections[s.id] = "none";
    });
    setSelections(nextSelections);
    // Clear customPermissions too — a full reset shouldn't preserve
    // permissions that fell outside the matrix on a prior save.
    setCustomPermissions([]);
    emit(nextSelections, []);
  };

  const handleRemoveCustomPermission = (perm) => {
    if (disabled) return;
    const next = customPermissions.filter((p) => p !== perm);
    setCustomPermissions(next);
    emit(selections, next);
  };

  const userSetCount = ALL_SUB_ITEMS.filter(
    (s) => (selections[s.id] || "none") !== "none",
  ).length;

  // Minimum level each row is forced to by another row's auto-bumps, and which
  // row forces it. Drives the lock affordance + below-floor disabling so a
  // silent snap-back becomes an explained constraint. Read-only — does not
  // affect what applyAutoBumps grants. (BL-058)
  const floors = useMemo(() => deriveFloors(selections), [selections]);

  return (
    <div className="picker-card">
      <div className="picker-card-header">
        <div className="picker-title">Access Configuration</div>
        <div className="picker-header-right">
          <div className="picker-count">
            <strong>{userSetCount}</strong> of {ALL_SUB_ITEMS.length} sub-areas
            configured
          </div>
          <button
            type="button"
            className="picker-reset-btn"
            onClick={handleResetAll}
            disabled={disabled || userSetCount === 0}
            title="Set every sub-area back to None"
          >
            Reset all
          </button>
        </div>
      </div>
      <div className="matrix-info-banner">
        <Icon name="info" size={14} aria-hidden="true" /> <strong>None</strong> hides the page. <strong>View</strong> =
        read-only access. <strong>Edit</strong> = full control.
      </div>

      {MATRIX_ROWS.map((row) => (
        <div key={row.id} className="matrix-section">
          <div className="matrix-section-header">
            <div className="matrix-section-title">
              <span aria-hidden="true">{SECTION_ICON[row.id] ? <Icon name={SECTION_ICON[row.id]} size={16} /> : "•"}</span>
              {row.label}
            </div>
            <div className="matrix-section-meta">
              {row.subItems.length} sub-area
              {row.subItems.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="matrix-rows">
            {row.subItems.map((subItem) => {
              const userLevel = selections[subItem.id] || "none";
              const floor = floors[subItem.id];
              // Name the row the admin can actually lower — for a transitive
              // floor the direct forcer is itself locked (BL-058 impl-review).
              const floorRoots = floor
                ? floorRootSources(floors, subItem.id)
                : [];
              const floorSourceText = formatFloorSources(
                floorRoots.length ? floorRoots : floor ? floor.sources : [],
              );

              return (
                <div key={subItem.id} className="matrix-row">
                  <div className="matrix-row-label">
                    <span>{subItem.label}</span>
                    {floor && (
                      <PwPopover
                        ariaLabel={`Why ${subItem.label} has a minimum level`}
                        align="start"
                        trigger={
                          <span className="matrix-floor-lock">
                            <Icon name="lock" size={12} aria-hidden="true" />
                            Min: {LEVEL_LABEL[floor.level]}
                          </span>
                        }
                      >
                        <div className="matrix-floor-pop">
                          <div className="matrix-floor-pop__title">
                            Minimum: {LEVEL_LABEL[floor.level]}
                          </div>
                          <p className="matrix-floor-pop__body">
                            Required by {floorSourceText}.
                          </p>
                        </div>
                      </PwPopover>
                    )}
                  </div>
                  <div
                    className="matrix-segmented"
                    role="radiogroup"
                    aria-label={`${subItem.label} access`}
                  >
                    {LEVELS_DISPLAY.map((level) => {
                      const cellExists =
                        level === "none" || subItem.levels[level];
                      const isActive = userLevel === level;
                      const isBelowFloor =
                        floor && levelRank(level) < levelRank(floor.level);

                      if (!cellExists) {
                        const naTitle =
                          level === "view"
                            ? `${subItem.label} is action-only — has no View tier`
                            : `${subItem.label} has no separate Edit tier`;
                        return (
                          <button
                            key={level}
                            type="button"
                            className="seg-btn na"
                            disabled
                            aria-disabled="true"
                            title={naTitle}
                          >
                            —
                          </button>
                        );
                      }

                      return (
                        <button
                          key={level}
                          type="button"
                          role="radio"
                          aria-checked={isActive}
                          className={`seg-btn${isActive ? " active" : ""}${isBelowFloor ? " locked" : ""}`}
                          disabled={disabled || isBelowFloor}
                          title={
                            isBelowFloor
                              ? `Minimum ${LEVEL_LABEL[floor.level]} — required by ${floorSourceText}`
                              : undefined
                          }
                          onClick={() => handleLevelChange(subItem.id, level)}
                        >
                          {LEVEL_LABEL[level]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Advanced disclosure — surfaces permissions that don't fit any
          matrix cell. Per PRD §9 Phase 7 step 6: read-only list with
          per-row Remove. */}
      {customPermissions.length > 0 && (
        <details className="matrix-advanced-details">
          <summary className="matrix-advanced">
            <span className="matrix-advanced-label">
              <Icon name="wrench" size={14} aria-hidden="true" /> Advanced
              <span className="matrix-advanced-count">
                {customPermissions.length} custom permission
                {customPermissions.length === 1 ? "" : "s"}
              </span>
            </span>
            <span className="matrix-advanced-toggle">Show details</span>
          </summary>
          <div className="matrix-advanced-body">
            <p className="matrix-advanced-hint">
              These permissions don't fit any matrix cell. They were preserved
              from this role's prior configuration. Remove individually if no
              longer needed.
            </p>
            <ul className="matrix-advanced-list">
              {customPermissions.map((perm) => (
                <li key={perm} className="matrix-advanced-item">
                  <code className="perm-key">{perm}</code>
                  <button
                    type="button"
                    className="matrix-advanced-remove"
                    onClick={() => handleRemoveCustomPermission(perm)}
                    disabled={disabled}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </details>
      )}
    </div>
  );
};

export default MatrixPicker;
